import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, CheckCircle2, Phone, Mail, CreditCard, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

const BLANK = { first_name: "", last_name: "", course_id: "", age_group: "", phone: "", email: "", message: "", trial_date: "", website: "" };
const field = "mt-1 w-full bg-[color:var(--bg-elev)] border border-[color:var(--border)] rounded-lg px-3 py-2.5 text-sm text-[color:var(--text)] focus:outline-none focus:border-[color:var(--accent)] transition-colors";

const Field = ({ label, children }) => (
  <label className="block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}{children}</label>
);

export const EnrollForm = ({ courses, season, preselect, trialPrefill }) => {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [applied, setApplied] = useState(preselect);
  const [appliedTrial, setAppliedTrial] = useState(trialPrefill);
  const [trialDates, setTrialDates] = useState([]);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Derive the selected course from the parent's preselect (React-sanctioned "adjust state on prop change").
  if (preselect !== applied) {
    setApplied(preselect);
    if (preselect) setForm(f => ({ ...f, course_id: preselect }));
  }
  if (trialPrefill !== appliedTrial) {
    setAppliedTrial(trialPrefill);
    if (trialPrefill) setForm(f => ({ ...f, course_id: trialPrefill.course_id, trial_date: trialPrefill.date }));
  }

  useEffect(() => {
    if (!form.course_id || form.course_id === "unknown") { setTrialDates([]); return; }
    let alive = true;
    api.get(`/courses/${form.course_id}/trial-dates`).then(r => { if (alive) setTrialDates(r.data.dates || []); }).catch(() => { if (alive) setTrialDates([]); });
    return () => { alive = false; };
  }, [form.course_id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/courses/enroll", { ...form, trial_date: form.trial_date || null });
      setDone(data);
      toast.success("Pré-inscription envoyée !");
    } catch (err) {
      toast.error(apiError(err, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const payable = done.course_id !== "unknown" && Number(done.course_price) > 0;
    return (
      <div className="glass rounded-2xl p-8 text-center space-y-3" data-testid="enroll-success">
        <CheckCircle2 className="w-10 h-10 mx-auto text-[color:var(--accent)]" />
        <h3 className="font-display text-3xl">Merci {done.first_name} !</h3>
        <p className="text-sm text-[color:var(--text-muted)]">Ta demande pour <strong className="text-white">{done.course_name}</strong> est bien reçue. L'équipe te recontacte pour valider ta place.</p>
        {done.trial_date && <p className="text-sm text-[color:var(--accent)] inline-flex items-center gap-2" data-testid="enroll-trial-confirmed"><Ticket className="w-4 h-4" /> Cours d'essai gratuit le {new Date(`${done.trial_date}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} — offert et déduit du 1er paiement.</p>}
        {payable && (
          <div className="pt-3 space-y-2" data-testid="enroll-pay-block">
            <Link to={`/cours/paiement?enrollment=${done.id}`} data-testid="enroll-pay-link" className="inline-flex items-center justify-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest hover:brightness-110 transition"><CreditCard className="w-4 h-4" /> Régler l'inscription en ligne · dès {Number(done.course_price).toFixed(0)} €/mois</Link>
            <p className="text-[11px] text-[color:var(--text-muted)]">Formules mensuelle, trimestrielle ou annuelle + adhésion {Number(season?.membership_fee ?? 10).toFixed(0)} € · paiement sécurisé Stripe · place confirmée immédiatement + carte membre. Tu peux aussi régler plus tard via le lien envoyé par e-mail.</p>
          </div>
        )}
        <button type="button" onClick={() => { setDone(null); setForm(BLANK); }} data-testid="enroll-again" className="text-xs uppercase tracking-widest text-[color:var(--accent)] underline">Inscrire une autre personne</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-4" data-testid="enroll-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Prénom *"><input data-testid="enroll-first-name" required maxLength={60} value={form.first_name} onChange={set("first_name")} className={field} /></Field>
        <Field label="Nom *"><input data-testid="enroll-last-name" required maxLength={60} value={form.last_name} onChange={set("last_name")} className={field} /></Field>
        <Field label="Cours souhaité *">
          <select data-testid="enroll-course" required value={form.course_id} onChange={set("course_id")} className={field}>
            <option value="">— choisir —</option>
            {courses.map(c => <option key={c.id} value={c.id}>{`${c.name} (${["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"][c.weekday]})`}</option>)}
            <option value="unknown">Je ne sais pas encore</option>
          </select>
        </Field>
        <Field label="Tranche d'âge *">
          <select data-testid="enroll-age" required value={form.age_group} onChange={set("age_group")} className={field}>
            <option value="">— choisir —</option>
            <option value="-10 ans">-10 ans</option>
            <option value="+10 ans">+10 ans</option>
          </select>
        </Field>
        <Field label="Téléphone *"><input data-testid="enroll-phone" required type="tel" minLength={6} maxLength={30} value={form.phone} onChange={set("phone")} className={field} /></Field>
        <Field label="E-mail *"><input data-testid="enroll-email" required type="email" value={form.email} onChange={set("email")} className={field} /></Field>
      </div>
      <Field label="Message (optionnel)"><textarea data-testid="enroll-message" rows={3} maxLength={600} value={form.message} onChange={set("message")} className={field} /></Field>
      {form.course_id && form.course_id !== "unknown" && (season?.first_class_free ?? true) && (
        <div className="rounded-xl border border-[color:var(--accent)]/40 bg-[color:var(--accent)]/5 p-4" data-testid="enroll-trial-block">
          <Field label={<span className="inline-flex items-center gap-1.5"><Ticket className="w-3 h-3 text-[color:var(--accent)]" /> Cours d'essai gratuit (optionnel)</span>}>
            <select data-testid="enroll-trial-date" value={form.trial_date} onChange={set("trial_date")} className={field}>
              <option value="">— sans cours d'essai —</option>
              {trialDates.map(d => <option key={d.date} value={d.date}>{d.label}</option>)}
            </select>
          </Field>
          <p className="text-[10px] text-[color:var(--text-muted)] mt-2">Le 1er cours est offert : il est déduit du 1er paiement au prorata du mois (ex. 28 € × 2 séances payantes ÷ 3 prévues).</p>
        </div>
      )}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <div className="flex items-center gap-4 flex-wrap">
        <button data-testid="enroll-submit" disabled={busy} className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest hover:brightness-110 transition disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} J'envoie ma pré-inscription
        </button>
        <div className="text-xs text-[color:var(--text-muted)] flex items-center gap-4 flex-wrap">
          {(season?.phones || []).map(p => <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 hover:text-white"><Phone className="w-3 h-3" /> {p}</a>)}
          {season?.email && <a href={`mailto:${season.email}`} className="inline-flex items-center gap-1 hover:text-white"><Mail className="w-3 h-3" /> {season.email}</a>}
        </div>
      </div>
    </form>
  );
};
